import type { AuthHoldState, AuthLine, AuthLineLock, AuthorisationSummaryDto, PreAuthSourceState } from '@his/shared';

export type AuthorisationSummary = AuthorisationSummaryDto;

export type { AuthLine };

export type AuthSourceState = PreAuthSourceState | 'drafted';

export type SourceLock = 'open' | AuthLineLock;

type LineLike = Pick<AuthLine, 'state'> & Partial<Pick<AuthLine, 'lock' | 'locked' | 'needsApproval' | 'payerId'>>;

const OPEN_STATES = new Set<PreAuthSourceState>(['not_sent', 'cancelled']);
const WAITING_STATES = new Set<PreAuthSourceState>(['pending', 'pended']);
const ANSWERED_STATES = new Set<PreAuthSourceState>(['approved', 'partial', 'not_required']);

export function lockOf(line: LineLike | null | undefined): SourceLock {
  if (!line) return 'open';
  if (line.lock !== undefined) return line.lock ?? 'open';
  if (line.locked !== undefined) return line.locked ? 'sent' : 'open';
  return OPEN_STATES.has(line.state) ? 'open' : 'sent';
}

export function badgeStateOf(line: LineLike | null | undefined): AuthSourceState | null {
  if (!line) return null;
  return lockOf(line) === 'drafted' ? 'drafted' : line.state;
}

export function lockReasonKey(lock: SourceLock): 'locked_sent_for_authorisation' | 'locked_in_a_draft_request' | null {
  if (lock === 'sent') return 'locked_sent_for_authorisation';
  if (lock === 'drafted') return 'locked_in_a_draft_request';
  return null;
}

export function indexLines(lines: readonly AuthLine[] | undefined): Map<string, AuthLine> {
  const index = new Map<string, AuthLine>();
  for (const line of lines ?? []) index.set(line.sourceKey, line);
  return index;
}

export function lineFor(index: ReadonlyMap<string, AuthLine>, keys: readonly (string | null | undefined)[]): AuthLine | null {
  for (const key of keys) {
    if (!key) continue;
    const line = index.get(key);
    if (line) return line;
  }
  return null;
}

export function invoiceLineKeys(row: { id: string; orderSourceKey?: string | null }): string[] {
  return row.orderSourceKey ? [`invoice_item:${row.id}`, row.orderSourceKey] : [`invoice_item:${row.id}`];
}

export function isWaiting(line: LineLike): boolean {
  return lockOf(line) === 'drafted' || WAITING_STATES.has(line.state);
}

export function needsSending(line: LineLike): boolean {
  return line.needsApproval !== false && lockOf(line) !== 'drafted' && (OPEN_STATES.has(line.state) || line.state === 'rejected');
}

export interface PayerCounts {
  open: number;
  waiting: number;
  answered: number;
}

export function payerCounts(summary: Pick<AuthorisationSummary, 'lines'>, payerId: string): PayerCounts {
  const counts: PayerCounts = { open: 0, waiting: 0, answered: 0 };
  for (const line of summary.lines) {
    if (line.payerId !== payerId) continue;
    if (isWaiting(line)) counts.waiting += 1;
    else if (ANSWERED_STATES.has(line.state)) counts.answered += 1;
    else if (needsSending(line)) counts.open += 1;
  }
  return counts;
}

export interface BannerModel {
  state: Exclude<AuthHoldState, 'none'>;
  variant: 'warning' | 'default' | 'success';
  titleKey: 'on_hold_x_need_the_insurers_approval' | 'waiting_for_the_insurer_x_sent' | 'the_insurer_has_answered_for_every_service';
  count: number;
  showSend: boolean;
  note: 'manual' | 'automatic' | null;
}

type BannerInput = Pick<AuthorisationSummary, 'mode' | 'lines' | 'canSend'> & { hold: { state: AuthHoldState; counts?: Partial<AuthorisationSummary['hold']['counts']> } };

export function bannerFor(summary: BannerInput | null | undefined): BannerModel | null {
  if (!summary || summary.hold.state === 'none') return null;
  const counts = summary.hold.counts ?? {};
  if (summary.hold.state === 'needs_send') {
    return {
      state: 'needs_send',
      variant: 'warning',
      titleKey: 'on_hold_x_need_the_insurers_approval',
      count: counts.needsSend ?? summary.lines.filter(needsSending).length,
      showSend: summary.canSend,
      note: summary.mode === 'manual' ? 'manual' : 'automatic',
    };
  }
  if (summary.hold.state === 'waiting') {
    return {
      state: 'waiting',
      variant: 'default',
      titleKey: 'waiting_for_the_insurer_x_sent',
      count: counts.waiting ?? summary.lines.filter(isWaiting).length,
      showSend: false,
      note: summary.mode === 'manual' ? 'manual' : null,
    };
  }
  return {
    state: 'answered',
    variant: 'success',
    titleKey: 'the_insurer_has_answered_for_every_service',
    count: counts.answered ?? summary.lines.filter((line) => ANSWERED_STATES.has(line.state) || line.state === 'rejected').length,
    showSend: false,
    note: null,
  };
}

export function badgeVariant(state: AuthSourceState): 'success' | 'warning' | 'destructive' | 'muted' {
  if (state === 'approved' || state === 'not_required') return 'success';
  if (state === 'partial' || state === 'drafted' || WAITING_STATES.has(state)) return 'warning';
  if (state === 'rejected') return 'destructive';
  return 'muted';
}

export function stateLabelKey(state: AuthSourceState): `auth_state_${AuthSourceState}` {
  return `auth_state_${state}`;
}

export interface CoverageChoice {
  payerId: string;
  isActive: boolean;
  validFrom: string | null;
  validTo: string | null;
}

export function pickCoverage<T extends CoverageChoice>(rows: readonly T[] | undefined, payerId: string, today: string): T | null {
  const ofPayer = (rows ?? []).filter((row) => row.payerId === payerId);
  const inForce = ofPayer.find((row) => row.isActive && (!row.validFrom || row.validFrom.slice(0, 10) <= today) && (!row.validTo || row.validTo.slice(0, 10) >= today));
  return inForce ?? ofPayer.find((row) => row.isActive) ?? ofPayer[0] ?? null;
}

export function nphiesActionsFor(row: { payer: { onNphies?: boolean } }, nphiesModule: boolean): boolean {
  return nphiesModule && row.payer.onNphies === true;
}
